"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { Collection } from "@/lib/shopify/types";
import { useState, useRef, useEffect } from "react";

interface CollectionFilterProps {
  collections: Collection[];
  selectedCollection?: string;
}

export function CollectionFilter({ collections, selectedCollection }: CollectionFilterProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleCollectionChange = (collectionHandle: string) => {
    const params = new URLSearchParams(searchParams);
    
    if (collectionHandle === "all") {
      params.delete("collection");
    } else {
      params.set("collection", collectionHandle);
    }
    
    const queryString = params.toString();
    const newUrl = queryString ? `/shop?${queryString}` : "/shop";
    router.push(newUrl);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedCollectionTitle = selectedCollection
    ? collections.find((c) => c.handle === selectedCollection)?.title || "All Products"
    : "All Products";

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">Filter by Collection:</span>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:border-sage-500 focus:ring-2 focus:ring-sage-500 focus:border-sage-500 transition-colors flex items-center gap-2 min-w-[150px] sm:min-w-[200px] justify-between"
            aria-label="Filter products by collection"
            aria-expanded={isOpen}
          >
            <span>{selectedCollectionTitle}</span>
            <svg
              className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isOpen && (
            <div className="absolute top-full mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10 overflow-hidden animate-dropdown">
              <button
                onClick={() => handleCollectionChange("all")}
                className={`w-full px-4 py-2 text-left hover:bg-sage-50 transition-colors ${
                  !selectedCollection ? "bg-sage-100 text-sage-900 font-medium" : "text-gray-700"
                }`}
              >
                All Products
              </button>
              {collections.map((collection) => (
                <button
                  key={collection.handle}
                  onClick={() => handleCollectionChange(collection.handle)}
                  className={`w-full px-4 py-2 text-left hover:bg-sage-50 transition-colors ${
                    selectedCollection === collection.handle
                      ? "bg-sage-100 text-sage-900 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {collection.title}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {selectedCollection && (
        <button
          onClick={() => handleCollectionChange("all")}
          className="text-sm text-sage-600 hover:text-sage-700 font-medium transition-colors flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Clear Filter
        </button>
      )}
    </div>
  );
}
